/**
 * Back and the path, drawn in the header (#2349 §A, mock item 6).
 *
 * The route declares (`useDeclareRouteChrome`); this reads. It owns no model and
 * resolves no label — the ancestors are `record/trail.ts`'s, which is the
 * journey a relationship navigation actually wrote rather than a path guessed
 * from the URL.
 *
 * ── the narrow-width answer is ELISION, not a scrollbar ─────────────────────
 * §G forbids page-level horizontal overflow, and a header is the one row that
 * cannot scroll — it is where the hamburger, the search trigger and the account
 * menu live. So the intermediate ancestors are hidden by the stylesheet below
 * the shell's compact width and the LAST one stays, which is the one Back goes
 * to. Nothing is lost that the person cannot reach: Back is the same
 * destination, and it keeps its accessible name at every width.
 */
import { Link } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useRouteChrome } from './routeChrome'
/* The slot's styles live in the HEADER's stylesheet, deliberately. Its one
   responsive rule is the shell's compact boundary, and that literal is already
   written there once — restating it in a second module would be the JS/CSS
   drift UX-03 exists to prevent, one file further out (rule 25: a literal
   outside the conformance fixture is an unguarded literal). */
import s from './Topbar.module.css'

export function RouteChromeBar() {
  const chrome = useRouteChrome()
  const navigate = useNavigate()
  // Nothing declared ⇒ nothing drawn, and no reserved gap: the header's other
  // regions are flex items, so an absent slot costs no space rather than
  // leaving a hole where a list page's chrome would have been.
  if (!chrome || (!chrome.back && chrome.crumbs.length === 0)) return null

  return (
    <div className={s.chromeSlot} data-testid="route-chrome">
      {chrome.back && (
        <button type="button" className={s.chromeBack} data-testid="route-chrome-back"
          onClick={() => void navigate({ to: chrome.back!.to })}
          aria-label={`Back to ${chrome.back.label}`}>
          <ArrowLeft size={16} aria-hidden />
        </button>
      )}
      {chrome.crumbs.length > 0 && (
        <nav className={s.chromeCrumbs} aria-label="Breadcrumb" data-testid="route-chrome-crumbs">
          <ol className={s.chromeList}>
            {chrome.crumbs.map((c, i) => (
              <li key={c.to} className={s.chromeCrumb}
                /* The LAST ancestor survives the compact width, because it is
                   where Back goes — see the header. */
                data-last={i === chrome.crumbs.length - 1 || undefined}>
                <Link to={c.to} className={s.chromeLink}>{c.label}</Link>
                <span className={s.chromeSep} aria-hidden="true">›</span>
              </li>
            ))}
          </ol>
        </nav>
      )}
    </div>
  )
}
